import { useState, useEffect, useCallback, useRef } from 'react'
import AutoResizeTextarea from './AutoResizeTextarea'
import {
  Music, Sparkles, Loader2, Play, Pause, Download,
  Headphones, Disc3, AlertCircle, ChevronDown
} from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'

const styles = [
  { id: 'pop', label: 'Поп', emoji: '🎵' },
  { id: 'rock', label: 'Рок', emoji: '🎸' },
  { id: 'electronic', label: 'Электронная', emoji: '🎹' },
  { id: 'hip-hop', label: 'Хип-Хоп', emoji: '🎤' },
  { id: 'jazz', label: 'Джаз', emoji: '🎷' },
  { id: 'classical', label: 'Классическая', emoji: '🎻' },
  { id: 'ambient', label: 'Эмбиент', emoji: '🌊' },
  { id: 'lo-fi', label: 'Lo-Fi', emoji: '☕' },
]

const durations = [
  { value: 30, label: '30 сек' },
  { value: 60, label: '1 мин' },
  { value: 120, label: '2 мин' },
  { value: 180, label: '3 мин' },
]

// Информация о музыкальных моделях
const MIN_PROMPT_LENGTH = 10

// Интерфейс музыкальной модели
interface MusicModelItem {
  id: string
  name: string
  provider: string
  price: number
  desc: string
  bestVocal: boolean
}

// Функция для конвертации модели из БД в MusicModelItem
function toMusicItem(m: any, prices: Record<string, number>): MusicModelItem {
  const id = m.model_name
  const price = prices[id] || 10
  const name = m.display_name || m.model_name.split('/').pop() || m.model_name
  const provider = m.provider || 'AI'
  const isPaid = m.is_paid
  const sub = m.category_sub
  // Если есть category_sub — используем для определения типа
  // ace-step — это инструментальная модель, не вокальная
  const isVocal = !id.includes('ace-step') && (sub === 'vocal' || sub === 'song' || !!(name.toLowerCase().includes('music') && !name.toLowerCase().includes('instrument')))
  const bestVocal = isPaid && (isVocal || sub === 'vocal' || sub === 'song')
  const desc = bestVocal
    ? 'Песни с вокалом и структурой'
    : isPaid
      ? 'Инструментальная мелодия'
      : (id.includes('stable-audio') || id.includes('lyria2'))
        ? 'Инструментальная мелодия'
        : 'Бесплатная генерация'
  return { id, name, provider, price, desc, bestVocal }
}

// Интерфейс для трека из истории
interface HistoryTrack {
  id: string
  model: string
  prompt: string
  url: string
  tokens_spent: number
  created_at: string
  style?: string
  duration?: number
}

interface TrackItem {
  id: string
  model: string
  prompt: string
  url: string
  tokens_spent: number
  created_at: string
  style?: string
  duration?: number
}

export default function MusicGenerator() {
  const { user } = useAuthStore()
  const [prompt, setPrompt] = useState('')
  const [selectedStyle, setSelectedStyle] = useState(styles[0].id)
  const [duration, setDuration] = useState(60)
  const [isGenerating, setIsGenerating] = useState(false)
  const [tracks, setTracks] = useState<TrackItem[]>([])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [promptError, setPromptError] = useState(false)
  const [starBalance, setStarBalance] = useState(0)
  const [selectedModel, setSelectedModel] = useState('')
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [lyrics, setLyrics] = useState('')
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [musicModels, setMusicModels] = useState<MusicModelItem[]>([])
  const [musicPrices, setMusicPrices] = useState<Record<string, number>>({})

  // Загрузка музыкальных моделей с сервера
  useEffect(() => {
    Promise.all([
      client.get('/models/ai-models'),
      client.get('/admin/ai-models/calculated-prices'),
    ]).then(([modelsResp, pricesResp]) => {
      const items: any[] = (modelsResp.data || []).filter((m: any) => m.is_active && m.category === 'music')
      const rawPrices: Record<string, any> = pricesResp.data || {}
      const starPrices: Record<string, number> = {}
      for (const [k, v] of Object.entries(rawPrices)) {
        if (v && (v as any).unit === 'stars' && typeof (v as any).input === 'number') {
          starPrices[k] = (v as any).input
        }
      }
      setMusicPrices(starPrices)
      const converted = items.map(m => toMusicItem(m, starPrices))
      setMusicModels(converted)
      if (converted.length > 0) {
        setSelectedModel(selectedModel || converted[0].id)
      }
    }).catch(() => {
      // fallback на дефолтные
      const fallback: MusicModelItem[] = [
        { id: 'fal-ai/minimax-music/v2.6', name: 'MiniMax Music 2.6', provider: 'Fal.ai', price: 30, desc: 'Песни с вокалом и структурой', bestVocal: true },
        { id: 'fal-ai/minimax-music/v2.5', name: 'MiniMax Music 2.5', provider: 'Fal.ai', price: 30, desc: 'Песни с вокалом и структурой', bestVocal: true },
        { id: 'fal-ai/elevenlabs/music', name: 'ElevenLabs Music', provider: 'Fal.ai', price: 160, desc: 'Высокое качество, до 10 мин', bestVocal: true },
      ]
      setMusicModels(fallback)
      if (fallback.length > 0) setSelectedModel(fallback[0].id)
    })
  }, [])

  const currentModel = musicModels.find(m => m.id === selectedModel) || musicModels[0]
  const isVocalModel = currentModel?.bestVocal ?? false
  const totalCost = currentModel?.price ?? 0
  const VOCAL_MODELS = musicModels.filter(m => m.bestVocal)
  const NON_VOCAL_MODELS = musicModels.filter(m => !m.bestVocal)

  const handleGenerate = async () => {
    const trimmed = prompt.trim()
    if (trimmed.length < MIN_PROMPT_LENGTH) {
      setPromptError(true)
      return
    }
    setPromptError(false)
    if (isGenerating || starBalance < totalCost) return

    setIsGenerating(true)
    try {
      const { data } = await client.post('/music/generate', {
        prompt: prompt.trim(),
        model: selectedModel,
        style: selectedStyle,
        duration,
        lyrics: lyrics.trim() || undefined,
      })

      addTrackToTop({
        id: data.id,
        model: selectedModel,
        prompt: prompt.trim(),
        url: `/api/music/audio/${data.id}`,
        tokens_spent: data.tokens_spent,
        created_at: data.created_at,
        style: selectedStyle,
        duration,
      })
      setStarBalance(prev => prev - totalCost)
      toast.success('Трек создаётся!')
    } catch (err: any) {
      if (err?.response?.status === 402) {
        toast.error('Недостаточно звёзд. Пополните баланс в профиле.')
      } else {
        toast.error('Ошибка при генерации музыки. ⭐ Звёзды возвращены')
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handlePlay = (track: TrackItem) => {
    if (playingId === track.id) {
      audioRef.current?.pause()
      setPlayingId(null)
      return
    }
    if (audioRef.current) {
      audioRef.current.pause()
    }
    const audio = new Audio(track.url)
    audio.onerror = () => {
      setPlayingId(null)
      toast.error('Не удалось воспроизвести аудио')
    }
    audio.onended = () => setPlayingId(null)
    audioRef.current = audio
    audio.play().catch(() => {
      setPlayingId(null)
    })
    setPlayingId(track.id)
  }

  // Загрузка баланса звёзд
  const loadBalance = useCallback(async () => {
    try {
      const { data } = await client.get('/tokens/balance')
      if (data?.stars_balance !== undefined) setStarBalance(data.stars_balance)
    } catch { /* ignore */ }
  }, [])

  // Загрузка истории генераций
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await client.get('/music/generations')
      if (Array.isArray(data)) {
        setTracks(data.filter((t: HistoryTrack) => {
          // Показываем только треки с реальными аудио-URL (не data:image заглушки)
          return t.url && !t.url.startsWith('data:image/')
        }).map((t: HistoryTrack) => ({
          id: t.id,
          model: t.model,
          prompt: t.prompt,
          url: t.id ? `/api/music/audio/${t.id}` : t.url,
          tokens_spent: t.tokens_spent,
          created_at: t.created_at,
          style: undefined,
          duration: undefined,
        })))
      }
    } catch { /* ignore */ }
    setHistoryLoaded(true)
  }, [])

  // После генерации — добавить свежий трек наверх без перезагрузки всей истории
  const addTrackToTop = (track: TrackItem) => {
    setTracks(prev => [track, ...prev])
  }

  useEffect(() => {
    loadBalance()
    loadHistory()
  }, [loadBalance, loadHistory])

  const modelNameById = (id: string) => musicModels.find(m => m.id === id)?.name || id

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
            <Music className="w-6 h-6" />
            Генерация музыки
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Создавайте музыку и песни через AI
          </p>
        </div>
        {/* Баланс звёзд */}
        <div className="glass rounded-xl px-3 py-2 flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-gray-300">{starBalance.toLocaleString()}</span>
          <span className="text-gray-500">⭐</span>
        </div>
      </div>

      {/* Input area */}
      <div className="glass-card p-6 space-y-4">
        {/* Выбор модели (кастомный дропдаун) */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1">
            <Disc3 className="w-3 h-3" />
            Модель AI
          </label>
          <div className="relative">
            <button
              onClick={() => setShowModelSelector(!showModelSelector)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all w-full"
            >
              <Sparkles className="w-4 h-4 text-pink-400 shrink-0" />
              <span className="truncate">{currentModel?.name || 'Загрузка...'}</span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-500 truncate">{currentModel?.provider || ''}</span>
              <span className="ml-auto text-pink-400/70 text-xs font-mono">{currentModel?.price ?? 0}⭐</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>

            {showModelSelector && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowModelSelector(false)} />
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl z-50 p-2 space-y-0.5 max-h-80 overflow-y-auto whitespace-normal">
                  {VOCAL_MODELS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedModel(m.id); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        selectedModel === m.id
                          ? 'bg-pink-500/20 text-pink-300'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                      }`}
                    >
                      <span className="font-medium">{m.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{m.provider}</span>
                      <span className="float-right text-pink-400/60 text-[10px] font-mono"> {m.price}⭐</span>
                    </button>
                  ))}
                  {NON_VOCAL_MODELS.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setSelectedModel(m.id); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        selectedModel === m.id
                          ? 'bg-pink-500/20 text-pink-300'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                      }`}
                    >
                      <span className="font-medium">{m.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{m.provider}</span>
                      <span className="float-right text-pink-400/60 text-[10px] font-mono"> {m.price}⭐</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Prompt */}
        <div>
          <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1">
            <Headphones className="w-3 h-3" />
            Описание трека
            <span className="ml-auto text-[10px] text-gray-600">
              Важно: описание должно быть более {MIN_PROMPT_LENGTH} символов
            </span>
          </label>
          <AutoResizeTextarea
            rows={3}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value)
              if (promptError) setPromptError(false)
            }}
            placeholder={isVocalModel
              ? "Опишите песню (описание должно быть более 10 символов): жанр, настроение, темп, инструменты... Например: грустная баллада под фортепиано с женским вокалом"
              : "Опишите музыку (описание должно быть более 10 символов): жанр, настроение, инструменты, темп... Пример: спокойная фортепианная мелодия с эмбиент-подложкой"
            }
          />
          {promptError && (
            <p className="text-[11px] text-red-400 mt-1">
              Описание должно содержать не менее {MIN_PROMPT_LENGTH} символов
            </p>
          )}
        </div>

        {/* Текст песни (только для вокальных моделей) */}
        {isVocalModel && (
          <div>
            <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1">
              <Disc3 className="w-3 h-3" />
              Текст песни (необязательно)
            </label>
            <AutoResizeTextarea
              rows={4}
              value={lyrics}
              onChange={(e) => setLyrics(e.target.value)}
              placeholder="[Verse]&#10;Текст куплета...&#10;[Chorus]&#10;Текст припева..."
            />
          </div>
        )}

        {/* Style */}
        <div>
          <label className="block text-xs text-gray-500 mb-2">Стиль</label>
          <div className="flex flex-wrap gap-2">
            {styles.map((style) => (
              <button
                key={style.id}
                onClick={() => setSelectedStyle(style.id)}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all
                  ${selectedStyle === style.id
                    ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30'
                    : 'glass text-gray-400 hover:text-gray-200'
                  }
                `}
              >
                <span>{style.emoji}</span>
                <span>{style.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div>
          <label className="block text-xs text-gray-500 mb-2">Длительность</label>
          <div className="flex flex-wrap gap-2">
            {durations.map((d) => (
              <button
                key={d.value}
                onClick={() => setDuration(d.value)}
                className={`
                  px-4 py-1.5 rounded-lg text-xs transition-all
                  ${duration === d.value
                    ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30'
                    : 'glass text-gray-400 hover:text-gray-200'
                  }
                `}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Цена и кнопка */}
        <div className="flex items-center gap-4 pt-2">
          <div className="text-xs text-gray-500">
            Стоимость: <span className="text-pink-400 font-mono">{totalCost} ⭐</span>
            {starBalance < totalCost && (
              <span className="text-red-400 ml-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Недостаточно звёзд
              </span>
            )}
          </div>
          <button
            onClick={handleGenerate}
            disabled={prompt.trim().length < MIN_PROMPT_LENGTH || isGenerating || starBalance < totalCost}
            className="gradient-btn flex-1 py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Создание трека...
              </>
            ) : (
              <>
                <Disc3 className="w-4 h-4" />
                Создать трек — ✦{totalCost}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {tracks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-300">Созданные треки</h2>
            <button
              onClick={async () => {
                try {
                  await client.delete('/music/generations')
                  setTracks([])
                  toast.success('Список очищен')
                } catch {
                  toast.error('Ошибка при очистке')
                }
              }}
              className="text-[11px] text-gray-500 hover:text-red-400 transition-colors"
            >
              Очистить список
            </button>
          </div>
          <div className="space-y-3">
            {tracks.map((track: TrackItem) => (
              <div key={track.id} className="glass-card p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-purple-500/20 border border-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <Music className="w-6 h-6 text-pink-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-200 truncate">{track.prompt}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-gray-500">{modelNameById(track.model)}</span>
                    <span className="text-xs text-gray-500">· {track.style}</span>
                    {track.duration && <span className="text-xs text-gray-500">· {track.duration}с</span>}
                    <span className="text-xs text-gray-500">· {track.tokens_spent}⭐</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <a
                    href={track.url}
                    download={`track-${track.id}.mp3`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-xl glass text-gray-400 hover:text-pink-400 hover:bg-pink-500/10 transition-all"
                    title="Скачать"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    className={`p-2.5 rounded-xl transition-all ${
                      playingId === track.id
                        ? 'bg-pink-500/20 text-pink-400'
                        : 'glass text-gray-400 hover:text-green-400 hover:bg-green-500/10'
                    }`}
                    onClick={() => handlePlay(track)}
                  >
                    {playingId === track.id
                      ? <Pause className="w-4 h-4" />
                      : <Play className="w-4 h-4" />
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
